/**
 * Page-view tracking, wired to the router: each resolved navigation records
 * the deepest matched route. See `queue.ts`.
 */
import type { AnyRouter } from '@tanstack/react-router';

import { PageViewQueue, type PageViewQueueOptions } from './queue.js';
import { apiTransport } from './transport.js';

export { apiTransport, visitId } from './transport.js';
export {
  PageViewQueue,
  idParams,
  noTransport,
  type PageView,
  type PageViewTransport,
} from './queue.js';

/**
 * Records a page view for every resolved navigation of `router`, and starts
 * sending them to the API (unless `options.transport` says otherwise).
 *
 * @returns The queue and a function that stops tracking.
 */
export function trackPageViews(
  router: AnyRouter,
  options: PageViewQueueOptions = {},
): { queue: PageViewQueue; stop: () => void } {
  const queue = new PageViewQueue({ transport: apiTransport(), ...options });
  const unsubscribe = router.subscribe('onResolved', () => {
    const leaf = router.state.matches.at(-1);
    if (leaf) queue.record(String(leaf.routeId), leaf.params as Record<string, unknown>);
  });
  const stopQueue = queue.start();
  return {
    queue,
    stop: () => {
      unsubscribe();
      stopQueue();
    },
  };
}
